/**
 * RegenerateMenu — admin override to re-trigger the 6-pillar summary
 * generator for a single (match_id, for_user_id, locale) triple.
 *
 * Permission gate: `matches.summary.regenerate`.
 * Backend contract:
 *   POST /v1/admin/matches/:matchId/regenerate-summary
 *     body { for_user_id, locale: 'en'|'es', bypass_status_gate?: bool }
 *   429 on per-admin rate limit (10/min).
 *
 * Spec: docs/superpowers/specs/2026-05-28-admin-matches-summaries-design.md §7.2 / §6.7
 */
import { useState } from 'react';
import { useApiUrl } from '@refinedev/core';
import { adminFetch, AdminApiError } from '@/providers/admin-api';

interface UserRef {
  id: string;
  display_name?: string | null;
  email?: string | null;
}

interface Props {
  matchId: string;
  match: { user1: UserRef; user2: UserRef };
  onDone?: () => void;
}

type LoadKey = `${string}-${'en' | 'es'}`;

export function RegenerateMenu({ matchId, match, onDone }: Props) {
  const apiUrl = useApiUrl();
  const [open, setOpen] = useState(false);
  const [bypass, setBypass] = useState(false);
  const [loadingKey, setLoadingKey] = useState<LoadKey | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const regen = async (forUserId: string, locale: 'en' | 'es') => {
    const key: LoadKey = `${forUserId}-${locale}`;
    setLoadingKey(key);
    setErr(null);
    try {
      await adminFetch(
        `${apiUrl}/v1/admin/matches/${matchId}/regenerate-summary`,
        {
          method: 'POST',
          body: JSON.stringify({
            for_user_id: forUserId,
            locale,
            bypass_status_gate: bypass,
          }),
        },
      );
      onDone?.();
    } catch (e: unknown) {
      if (e instanceof AdminApiError && e.status === 429) {
        setErr('Rate-limited (10 regenerations / minute). Try again in a moment.');
      } else if (e instanceof AdminApiError) {
        setErr(e.message);
      } else {
        setErr('Regenerate failed');
      }
    } finally {
      setLoadingKey(null);
    }
  };

  const userLabel = (u: UserRef) =>
    u.display_name || u.email || u.id.slice(0, 8);

  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setOpen(o => !o)}
        type="button"
        className="cursor-pointer rounded border bg-background px-2.5 py-1.5 text-[13px] hover:bg-muted"
        aria-haspopup="true"
        aria-expanded={open}
      >
        ↻ Regenerate ▾
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-10 mt-1 min-w-[280px] rounded-md border bg-popover p-2 text-popover-foreground shadow-lg"
        >
          {[match.user1, match.user2].flatMap(u =>
            (['en', 'es'] as const).map(l => {
              const key: LoadKey = `${u.id}-${l}`;
              return (
                <button
                  key={key}
                  type="button"
                  disabled={loadingKey === key}
                  onClick={() => regen(u.id, l)}
                  className="block w-full cursor-pointer rounded border-none bg-transparent px-2 py-1.5 text-left text-[13px] hover:bg-muted"
                >
                  ↻ {userLabel(u)} ({l}){loadingKey === key ? '…' : ''}
                </button>
              );
            }),
          )}
          <label
            title="Allows regeneration for expired/historical matches; default off."
            className="mt-1 flex items-center gap-1.5 border-t px-2 py-1.5 text-xs text-muted-foreground"
          >
            <input
              type="checkbox"
              checked={bypass}
              onChange={e => setBypass(e.target.checked)}
            />
            Force (bypass status gate)
          </label>
          {err && (
            <div className="mt-1 rounded bg-destructive/10 px-2 py-1.5 text-xs text-destructive">
              {err}
            </div>
          )}
        </div>
      )}
    </span>
  );
}
